import styled, {css} from "styled-components";
import {brand, fontFamilies, radii} from "theme/tokens";

/**
 * Externalized styles for {@link ./PlanChangeDialog} (api#297): the prorated-amount body, the
 * loading state, and the ghost/primary action pair. Everything reads `theme.tokens`, so light + dark
 * follow the active mode for free — no hardcoded colors (epic #26 mandate).
 */

/** Confirmation copy: the prorated amount + immediate-change explanation (a flat catalogue string). */
export const Body = styled.p`
	margin: 0;
	padding: 0 24px 8px;
	font-size: 14px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Centered spinner + label shown while the prorated preview is being fetched. */
export const State = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 4px 24px 12px;
	font-size: 14px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The plan's main characteristic and the order note above an upgrade's order button
 * (kitecorp/transferify-ui#3394), on the body's margins.
 */
export const OrderNoteSlot = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 0 24px;
`;

/** The new plan's main characteristic, in the body's own type so it reads as part of the offer. */
export const Benefit = styled.p`
	margin: 0;
	font-size: 14px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The confirm, then the quiet Cancel under it, stacked at every width in the same order as the DOM
 * (owner decision 2026-10-09, kitecorp/transferify-ui#3394): the information above an upgrade's order
 * button then runs straight into the button, with nothing between them. `./PlanChangeDialog` explains why
 * the one control under the order button is compatible with § 312j BGB.
 */
export const Actions = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 4px;
	padding: 16px 24px 20px;
`;

const buttonBase = css`
	height: 40px;
	padding: 0 16px;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: 0.16s;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	&:disabled {
		cursor: not-allowed;
	}
`;

/**
 * The quiet secondary action under the confirm: no fill and no border, in the secondary ink, so the one
 * control after an order button cannot be mistaken for a second way to order.
 */
export const QuietButton = styled.button.attrs({type: "button"})`
	${buttonBase};
	border: none;
	background: transparent;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink2};

	&:hover:not(:disabled) {
		color: ${({theme}) => theme.tokens.ink};
		text-decoration: underline;
	}
`;

/** Primary confirm action (v3 CTA): brand-blue fill, hover shade, token disabled surface. */
export const ConfirmButton = styled.button.attrs({type: "button"})`
	${buttonBase};
	border: none;
	background: ${({theme}) => theme.tokens.blue};
	color: ${brand.white};

	&:hover:not(:disabled) {
		background: ${({theme}) => theme.tokens.blued};
	}

	&:disabled {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
	}
`;
